export const IMAGE_FILTER_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'grayscale', label: 'Grayscale' },
  { value: 'sepia', label: 'Sepia' },
  { value: 'vintage', label: 'Vintage' },
  { value: 'high-contrast', label: 'High Contrast' },
  { value: 'warm', label: 'Warm' },
  { value: 'cool', label: 'Cool' },
  { value: 'blur', label: 'Blur' },
  { value: 'sketch', label: 'Sketch' },
] as const;

export type ImageFilterName = (typeof IMAGE_FILTER_OPTIONS)[number]['value'];

const canvasFilterMap: Record<Exclude<ImageFilterName, 'none' | 'sketch'>, string> = {
  grayscale: 'grayscale(1)',
  sepia: 'sepia(1)',
  vintage: 'sepia(0.55) contrast(1.08) saturate(0.85) brightness(0.98)',
  'high-contrast': 'contrast(1.45) saturate(1.1)',
  warm: 'sepia(0.25) saturate(1.15) hue-rotate(-8deg)',
  cool: 'saturate(0.9) hue-rotate(12deg) contrast(1.05)',
  blur: 'blur(1.4px)',
};

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function drawSketchEffect(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  const src = ctx.getImageData(0, 0, width, height);
  const srcData = src.data;
  const total = width * height;
  const gray = new Float32Array(total);

  for (let i = 0; i < total; i += 1) {
    const p = i * 4;
    const r = srcData[p];
    const g = srcData[p + 1];
    const b = srcData[p + 2];
    gray[i] = r * 0.299 + g * 0.587 + b * 0.114;
  }

  const out = ctx.createImageData(width, height);
  const outData = out.data;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      const p = i * 4;
      const alpha = srcData[p + 3];

      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
        const edge = 250;
        outData[p] = edge;
        outData[p + 1] = edge;
        outData[p + 2] = edge;
        outData[p + 3] = alpha;
        continue;
      }

      const tl = gray[(y - 1) * width + (x - 1)];
      const tc = gray[(y - 1) * width + x];
      const tr = gray[(y - 1) * width + (x + 1)];
      const ml = gray[y * width + (x - 1)];
      const mr = gray[y * width + (x + 1)];
      const bl = gray[(y + 1) * width + (x - 1)];
      const bc = gray[(y + 1) * width + x];
      const br = gray[(y + 1) * width + (x + 1)];

      const gx = -tl + tr - 2 * ml + 2 * mr - bl + br;
      const gy = tl + 2 * tc + tr - bl - 2 * bc - br;
      const edgeStrength = Math.min(255, Math.sqrt(gx * gx + gy * gy) * 1.2);
      const sketch = Math.max(0, 255 - edgeStrength);

      outData[p] = sketch;
      outData[p + 1] = sketch;
      outData[p + 2] = sketch;
      outData[p + 3] = alpha;
    }
  }

  ctx.putImageData(out, 0, 0);
}

export async function applyImageFilter(blob: Blob, filterName: ImageFilterName): Promise<Blob> {
  if (filterName === 'none') {
    return blob;
  }

  const bitmap = await createImageBitmap(blob);
  const canvas = createCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    bitmap.close();
    throw new Error('2D context is not available');
  }

  if (filterName === 'sketch') {
    ctx.drawImage(bitmap, 0, 0);
    drawSketchEffect(ctx, canvas.width, canvas.height);
  } else {
    ctx.filter = canvasFilterMap[filterName];
    ctx.drawImage(bitmap, 0, 0);
  }

  bitmap.close();

  const outBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(result => {
      if (result) {
        resolve(result);
        return;
      }
      reject(new Error('Failed to render filtered image'));
    }, 'image/png');
  });

  return outBlob;
}
